import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, Mail, LogOut, Shield, Bell, Trash2,
  Globe, Users, Lock, Eye
} from 'lucide-react';

const Settings = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [purchaseVisibility, setPurchaseVisibility] = useState(user?.purchase_visibility || 'private');
  const [saving, setSaving] = useState(false);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.patch('/auth/profile', {
        name,
        bio,
        purchase_visibility: purchaseVisibility,
      });
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      toast.error('Account deletion is not available in this demo');
    }
  };

  const VISIBILITY_OPTIONS = [
    { id: 'public', icon: Globe, label: 'Public', desc: 'Anyone can see your purchases' },
    { id: 'followers', icon: Users, label: 'Followers Only', desc: 'Only followers can see' },
    { id: 'private', icon: Lock, label: 'Private', desc: 'Only you can see' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Dashboard
            </button>
            <h1 className="font-heading font-bold">Settings</h1>
            <div className="w-16" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Profile Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <User className="w-5 h-5 text-primary" />
            <h2 className="font-heading text-lg font-semibold">Profile</h2>
          </div>

          <div className="flex items-center gap-6 mb-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-pink-500 to-violet-600 flex items-center justify-center text-2xl font-bold text-white">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                user?.name?.charAt(0)?.toUpperCase() || 'U'
              )}
            </div>
            <div>
              <p className="font-semibold text-lg">{user?.name}</p>
              <p className="text-muted-foreground">{user?.email}</p>
              {user?.streak_days > 0 && (
                <p className="text-accent text-sm mt-1">🔥 {user.streak_days} day streak</p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm text-muted-foreground">Display Name</Label>
              <Input 
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 bg-input/50 border-transparent focus:border-primary rounded-xl"
                data-testid="name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bio" className="text-sm text-muted-foreground">Bio</Label>
              <Textarea 
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="What kind of deals do you hunt?"
                className="bg-input/50 border-transparent focus:border-primary rounded-xl resize-none"
                rows={3}
                data-testid="bio-input"
              />
            </div>
            <Button 
              onClick={handleSaveProfile}
              disabled={saving}
              className="rounded-xl bg-primary text-primary-foreground"
              data-testid="update-profile-btn"
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </motion.div>

        {/* Purchase Privacy */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <Eye className="w-5 h-5 text-primary" />
            <h2 className="font-heading text-lg font-semibold">Purchase History Visibility</h2>
          </div>

          <p className="text-sm text-muted-foreground mb-4">
            Control who can see your purchase history on your profile.
          </p>

          <div className="space-y-3">
            {VISIBILITY_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => setPurchaseVisibility(option.id)}
                className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all ${
                  purchaseVisibility === option.id
                    ? 'bg-primary/10 border-2 border-primary'
                    : 'bg-card/50 border border-border/50 hover:border-border'
                }`}
                data-testid={`visibility-${option.id}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  purchaseVisibility === option.id ? 'bg-primary/20' : 'bg-muted'
                }`}>
                  <option.icon className={`w-5 h-5 ${
                    purchaseVisibility === option.id ? 'text-primary' : 'text-muted-foreground'
                  }`} />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium">{option.label}</p>
                  <p className="text-sm text-muted-foreground">{option.desc}</p>
                </div>
                {purchaseVisibility === option.id && (
                  <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                    <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Email Settings */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <Mail className="w-5 h-5 text-primary" />
            <h2 className="font-heading text-lg font-semibold">Email</h2>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-card/50">
            <div>
              <p className="font-medium">{user?.email}</p>
              <p className="text-sm text-muted-foreground">Primary email address</p>
            </div>
            <span className="px-3 py-1 rounded-full bg-success/20 text-success text-xs font-medium">
              Verified
            </span>
          </div>
        </motion.div>

        {/* Notification Preferences */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <Bell className="w-5 h-5 text-primary" />
            <h2 className="font-heading text-lg font-semibold">Notifications</h2>
          </div>

          <p className="text-muted-foreground">
            Notification preferences are configured per tracker. Visit each tracker's detail page to customize alerts.
          </p>
        </motion.div>

        {/* Security */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-5 h-5 text-primary" />
            <h2 className="font-heading text-lg font-semibold">Security</h2>
          </div>

          <div className="space-y-3">
            <Button 
              variant="outline"
              className="w-full justify-start rounded-xl h-12"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sign Out
            </Button>
            <Button 
              variant="outline"
              className="w-full justify-start rounded-xl h-12 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleDeleteAccount}
              data-testid="delete-account-btn"
            >
              <Trash2 className="w-4 h-4 mr-3" />
              Delete Account
            </Button>
          </div>
        </motion.div>

        {/* App Info */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center py-6"
        >
          <p className="text-sm text-muted-foreground">
            DealPing v2.0.0
          </p>
          <p className="text-xs text-muted-foreground/60 mt-1">
            Social deal hunting made fun
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default Settings;
